import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  notification,
  Tag,
} from 'antd';
import CorporateClientStatusSelect from '../../../../components/Select/CorporateClientStatusSelect';
import InternalUserSelect from '../../../../components/Select/InternalUserSelect';
import { PaymentProcessors } from '../../../../constants/PaymentProcessors';
import { Link, useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import routes from '../../../../constants/routes';
import { useMutation } from '@apollo/client';
import {
  MUTATION_UPDATE_CORPORATE_CLIENT,
  MUTATION_SET_CORPORATE_CLIENT_USEFULL_AE,
  MUTATION_UPDATE_PAYMENT_CONFIG,
  QUERY_LIST_CORPORATE_CLIENTS_TABLE,
  QUERY_ONE_CORPORATE_CLIENT,
} from '../../../../queries/CorporateClientsQueries';

import { GeneralTabProps } from '../utils/corpClientTypes';
import AdminRoles from 'constants/AdminRoles';

const GeneralTab: React.FC<GeneralTabProps> = ({
  user,
  corpClientData,
  refetch,
}) => {
  const params = useParams();
  const [saving, setSaving] = useState(false);
  const [updatingAE, setUpdatingAE] = useState(false);
  const [updatingPaymentConfig, setUpdatingPaymentConfig] = useState(false);
  const [selectedAE, setSelectedAE] = useState<string | number | null>(null);
  const [form] = Form.useForm();
  const [paymentConfigForm] = Form.useForm();
  const { dbUser } = user;
  const [api, contextHolder] = notification.useNotification();
  const [hasChanges, setHasChanges] = useState(false);
  const [hasPaymentConfigChanges, setHasPaymentConfigChanges] = useState(false);

  const [updateCorporateClient] = useMutation(
    MUTATION_UPDATE_CORPORATE_CLIENT,
    {
      refetchQueries: [{ query: QUERY_LIST_CORPORATE_CLIENTS_TABLE }],
    },
  );

  const [setCorporateClientUsefullAE] = useMutation(
    MUTATION_SET_CORPORATE_CLIENT_USEFULL_AE,
    {
      refetchQueries: [
        {
          query: QUERY_ONE_CORPORATE_CLIENT,
          variables: { corporate_client_id: params.corporate_client_id },
        },
      ],
    },
  );

  const [updatePaymentConfig] = useMutation(MUTATION_UPDATE_PAYMENT_CONFIG, {
    refetchQueries: [
      {
        query: QUERY_ONE_CORPORATE_CLIENT,
        variables: { corporate_client_id: params.corporate_client_id },
      },
    ],
  });

  const corpClient = corpClientData?.getCorporateClient;
  const link = `${routes.TV_HOSTED_PAGE}/${corpClient?.uuid}`;

  const prevStatusRef = useRef<string | null>(
    corpClient?.status_id !== undefined
      ? corpClient.status_id.toString()
      : null,
  );

  useEffect(() => {
    prevStatusRef.current =
      corpClient?.status_id !== undefined
        ? corpClient.status_id.toString()
        : null;
  }, [corpClient?.status_id, form]);

  const checkFormChanges = useCallback(() => {
    const currentValues = form.getFieldsValue();
    const initialValues = {
      name: corpClient?.name,
      short_name: corpClient?.short_name,
      status_id: corpClient?.status_id?.toString(),
      lock_code: corpClient?.lock_code,
      locked: corpClient?.locked,
      enable_missing_container_detection:
        corpClient?.enable_missing_container_detection,
    };

    const hasFormChanges = Object.keys(initialValues).some(
      (key) =>
        currentValues[key] !== initialValues[key as keyof typeof initialValues],
    );

    setHasChanges(hasFormChanges);
  }, [form, corpClient]);

  useEffect(() => {
    checkFormChanges();
  }, [checkFormChanges]);

  const checkPaymentConfigChanges = useCallback(() => {
    const currentValues = paymentConfigForm.getFieldsValue();
    const initialValues = {
      login_name: corpClient?.config_parsed?.payment_config?.login_name || '',
      card_name: corpClient?.config_parsed?.payment_config?.card_name || '',
    };

    const hasFormChanges = Object.keys(initialValues).some(
      (key) =>
        currentValues[key] !== initialValues[key as keyof typeof initialValues],
    );

    setHasPaymentConfigChanges(hasFormChanges);
  }, [paymentConfigForm, corpClient]);

  useEffect(() => {
    checkPaymentConfigChanges();
  }, [checkPaymentConfigChanges]);

  const validateDigits = (rule: any, value: string) => {
    const regExp = /^[0-9]{4}$/; // Regex for exactly 4 digits
    if (value && !regExp.test(value)) {
      return Promise.reject('Please enter exactly 4 digits');
    }
    return Promise.resolve();
  };

  const handleEditSubmit = useCallback(
    async (values: any) => {
      setSaving(true);
      try {
        const initialValues = {
          name: corpClient?.name,
          short_name: corpClient?.short_name,
          status_id: corpClient?.status_id?.toString(),
          lock_code: corpClient?.lock_code,
          locked: corpClient?.locked,
          enable_missing_container_detection:
            corpClient?.enable_missing_container_detection,
        };

        const changedValues: any = {};
        Object.keys(values).forEach((key) => {
          if (
            values[key] !== initialValues[key as keyof typeof initialValues]
          ) {
            if (key === 'status_id') {
              changedValues[key] = Number(values[key]);
            } else {
              changedValues[key] = values[key];
            }
          }
        });

        await updateCorporateClient({
          variables: {
            corporate_client_id: params.corporate_client_id,
            input: changedValues,
          },
        });
        api.success({ message: 'Corporate client updated successfully' });
        setHasChanges(false);
        if (refetch) {
          refetch();
        }
      } catch (error) {
        api.error({ message: 'Failed to update corporate client' });
      } finally {
        setSaving(false);
      }
    },
    [updateCorporateClient, params, api, refetch, corpClient],
  );

  const handleUpdateAccountExecutive = useCallback(
    async (userId: string | number) => {
      try {
        setUpdatingAE(true);
        await setCorporateClientUsefullAE({
          variables: {
            corporate_client_id: params.corporate_client_id,
            user_id: userId,
          },
        });
        api.success({ message: 'Account Executive updated successfully' });
        setSelectedAE(null);
        if (refetch) {
          refetch();
        }
      } catch (error) {
        api.error({ message: 'Failed to update Account Executive' });
      } finally {
        setUpdatingAE(false);
      }
    },
    [setCorporateClientUsefullAE, params.corporate_client_id, api],
  );

  const handleUpdatePaymentConfig = useCallback(
    async (values: {
      login_name?: string | null;
      card_name?: string | null;
    }) => {
      try {
        setUpdatingPaymentConfig(true);

        const initialValues = {
          login_name:
            corpClient?.config_parsed?.payment_config?.login_name || '',
          card_name: corpClient?.config_parsed?.payment_config?.card_name || '',
        };

        const variables: any = {
          corporate_client_id: params.corporate_client_id,
        };

        Object.keys(values).forEach((key) => {
          if (
            values[key as keyof typeof values] !==
            initialValues[key as keyof typeof initialValues]
          ) {
            variables[key] = values[key as keyof typeof values] || null;
          }
        });

        await updatePaymentConfig({
          variables,
        });
        api.success({ message: 'Payment configuration updated successfully' });
        setHasPaymentConfigChanges(false);
        if (refetch) {
          refetch();
        }
      } catch (error) {
        api.error({ message: 'Failed to update payment configuration' });
      } finally {
        setUpdatingPaymentConfig(false);
      }
    },
    [updatePaymentConfig, params.corporate_client_id, api, refetch, corpClient],
  );

  return (
    <>
      {contextHolder}
      <Descriptions column={1} bordered>
        <Descriptions.Item>
          <Form
            form={form}
            name="name"
            onFinish={handleEditSubmit}
            onValuesChange={checkFormChanges}
            initialValues={{
              name: corpClient?.name,
              short_name: corpClient?.short_name,
              status_id: corpClient?.status_id?.toString(),
              lock_code: corpClient?.lock_code,
              locked: corpClient?.locked,
              enable_missing_container_detection:
                corpClient?.enable_missing_container_detection,
            }}
          >
            <div style={{ display: 'flex', gap: '20px' }}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    type: 'string',
                    required: true,
                    min: 1,
                    message: 'cannot be empty',
                  },
                ]}
                style={{ marginBottom: 10, width: '300px' }}
              >
                <Input
                  placeholder={corpClient?.name}
                  disabled={saving || !dbUser?.userPermissions.DangerZone}
                />
              </Form.Item>
              <Form.Item
                name="short_name"
                label="Short Name"
                rules={[
                  {
                    type: 'string',
                    max: 50,
                    message: 'Short name cannot exceed 50 characters',
                  },
                ]}
                style={{ marginBottom: 10, width: '200px' }}
              >
                <Input
                  placeholder={corpClient?.short_name || 'Enter short name'}
                  disabled={saving || !dbUser?.userPermissions.DangerZone}
                />
              </Form.Item>
              <Form.Item
                label="Status"
                name="status_id"
                rules={[{ required: true, message: 'Please select status!' }]}
                style={{ marginBottom: 10, width: '200px' }}
              >
                <CorporateClientStatusSelect
                  placeholder="Select status"
                  prevValue={prevStatusRef.current}
                  onChange={(val: any) => {
                    prevStatusRef.current = form.getFieldValue('status_id');
                    form.setFieldValue('status_id', val);
                  }}
                  disabled={!dbUser?.userPermissions.DangerZone}
                />
              </Form.Item>
            </div>
            <br />
            <div style={{ display: 'flex', gap: '20px' }}>
              <Form.Item
                name="locked"
                label="Locked"
                valuePropName="checked"
                style={{ marginBottom: 10 }}
              >
                <Checkbox
                  disabled={saving || !dbUser?.userPermissions.DangerZone}
                />
              </Form.Item>
              <Form.Item
                name="lock_code"
                label="Lock Code"
                rules={[
                  ({ getFieldValue }) => ({
                    type: 'string',
                    max: 4,
                    required: !!getFieldValue('locked'),
                    message: 'Please set the code if locked is checked!',
                  }),
                  {
                    validator: validateDigits, // Custom validator for 4 digits
                  },
                ]}
                style={{ marginBottom: 10, width: '150px' }}
              >
                <Input
                  maxLength={4}
                  placeholder={corpClient?.lock_code}
                  disabled={saving || !dbUser?.userPermissions.DangerZone}
                />
              </Form.Item>
            </div>
            <br />

            <div style={{ display: 'flex', gap: '20px' }}>
              <Form.Item
                name="enable_missing_container_detection"
                label="Enable Missing Container Detection"
                valuePropName="checked"
                style={{ marginBottom: 10 }}
              >
                <Checkbox
                  disabled={saving || !dbUser?.userPermissions.DangerZone}
                />
              </Form.Item>
            </div>

            <Button
              loading={saving}
              type="primary"
              htmlType="submit"
              disabled={!dbUser?.userPermissions.DangerZone || !hasChanges}
            >
              Update
            </Button>
          </Form>
        </Descriptions.Item>
        {!!corpClient?.config_parsed?.payment_config
          .default_payment_processor_id && (
          <Descriptions.Item label="Campus Card Provider">
            <div>
              <Tag
                color="blue"
                style={{
                  fontSize: '14px',
                  padding: '4px 8px',
                  marginBottom: '10px',
                }}
              >
                {
                  PaymentProcessors[
                    corpClient?.config_parsed?.payment_config
                      ?.default_payment_processor_id
                  ]
                }
              </Tag>
              <Form
                form={paymentConfigForm}
                layout="inline"
                onFinish={handleUpdatePaymentConfig}
                onValuesChange={checkPaymentConfigChanges}
                initialValues={{
                  login_name:
                    corpClient?.config_parsed?.payment_config?.login_name || '',
                  card_name:
                    corpClient?.config_parsed?.payment_config?.card_name || '',
                }}
                style={{ marginTop: '10px' }}
              >
                <Form.Item name="login_name" label="Login Name">
                  <Input
                    placeholder="Enter login name"
                    disabled={
                      updatingPaymentConfig ||
                      !dbUser?.userPermissions.DangerZone
                    }
                    style={{ width: '200px' }}
                  />
                </Form.Item>
                <Form.Item name="card_name" label="Card Name">
                  <Input
                    placeholder="Enter card name"
                    disabled={
                      updatingPaymentConfig ||
                      !dbUser?.userPermissions.DangerZone
                    }
                    style={{ width: '200px' }}
                  />
                </Form.Item>
                <Button
                  loading={updatingPaymentConfig}
                  type="primary"
                  htmlType="submit"
                  disabled={
                    !dbUser?.userPermissions.DangerZone ||
                    !hasPaymentConfigChanges
                  }
                >
                  Update Payment Config
                </Button>
              </Form>
              <div
                style={{
                  marginTop: '25px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                }}
              >
                <div>
                  <strong style={{ color: '#495057', marginRight: '15px' }}>
                    Refund Strategy:
                  </strong>
                  {(() => {
                    const refundStrategies =
                      corpClient?.config_parsed?.payment_config
                        ?.refund_strategies;
                    const currentProcessorId =
                      corpClient?.config_parsed?.payment_config
                        ?.default_payment_processor_id;

                    if (!refundStrategies || refundStrategies.length === 0) {
                      return (
                        <Tag
                          color="blue"
                          style={{ fontSize: '14px', padding: '2px 8px' }}
                        >
                          Default
                        </Tag>
                      );
                    }

                    const currentStrategy = refundStrategies.find(
                      (strategy) =>
                        strategy.processor_id === currentProcessorId,
                    );

                    if (
                      !currentStrategy ||
                      currentStrategy.strategy === 'Default'
                    ) {
                      return (
                        <Tag
                          color="blue"
                          style={{ fontSize: '14px', padding: '2px 8px' }}
                        >
                          Default
                        </Tag>
                      );
                    }
                    if (currentStrategy.strategy === 'Email') {
                      return (
                        <Tag
                          color="green"
                          style={{ fontSize: '14px', padding: '2px 8px' }}
                        >
                          Email
                        </Tag>
                      );
                    }
                    return (
                      <Tag
                        color="blue"
                        style={{ fontSize: '12px', padding: '2px 8px' }}
                      >
                        Default
                      </Tag>
                    );
                  })()}
                </div>
                {(() => {
                  const refundStrategies =
                    corpClient?.config_parsed?.payment_config
                      ?.refund_strategies;
                  const currentProcessorId =
                    corpClient?.config_parsed?.payment_config
                      ?.default_payment_processor_id;
                  const currentStrategy = refundStrategies?.find(
                    (strategy) => strategy.processor_id === currentProcessorId,
                  );

                  if (
                    currentStrategy?.strategy === 'Email' &&
                    currentStrategy.email
                  ) {
                    return (
                      <div style={{ marginLeft: '0px', marginTop: '10px' }}>
                        <div
                          style={{
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span
                            style={{
                              color: '#6c757d',
                              fontSize: '13px',
                              minWidth: '120px',
                            }}
                          >
                            Email Destination:
                          </span>
                          <span style={{ color: '#495057', fontWeight: '500' }}>
                            {currentStrategy.email.to || 'Not set'}
                          </span>
                        </div>
                        <div
                          style={{ display: 'flex', alignItems: 'flex-start' }}
                        >
                          <span
                            style={{
                              color: '#6c757d',
                              fontSize: '13px',
                              minWidth: '120px',
                            }}
                          >
                            User Note:
                          </span>
                          <span style={{ color: '#495057', fontWeight: '500' }}>
                            {currentStrategy.email.user_note || 'Not set'}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="TV dashboard URL">
          <span style={{ marginRight: 50 }}>
            {process.env.REACT_APP_URI}
            {link}
          </span>
          <Link to={link + '?admin=true'}>Preview</Link>

          <Link
            to={`${link}/share?admin=true`}
            target="_blank"
            style={{ marginLeft: 50 }}
          >
            Generate Shareable Image
          </Link>
        </Descriptions.Item>
        <Descriptions.Item label="Account Executive">
          {dbUser?.role_id === AdminRoles.InternalAdministrator ||
          dbUser?.role_id === AdminRoles.SuperAdmin ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <InternalUserSelect
                value={selectedAE || corpClient?.usefull_ae_id}
                onChange={(value) => {
                  setSelectedAE(value);
                }}
                placeholder="Select Account Executive"
                disabled={updatingAE}
                style={{ width: 300 }}
              />
              <Button
                loading={updatingAE}
                type="primary"
                disabled={!selectedAE}
                onClick={() => {
                  if (selectedAE) {
                    handleUpdateAccountExecutive(selectedAE);
                  }
                }}
              >
                Update
              </Button>
            </div>
          ) : (
            <div>
              {corpClient?.usefullAE ? (
                <a href={`mailto:${corpClient.usefullAE.email}`}>
                  {corpClient.usefullAE.full_name} ({corpClient.usefullAE.email}
                  )
                </a>
              ) : (
                <span style={{ color: '#999' }}>
                  No Account Executive assigned
                </span>
              )}
            </div>
          )}
          {corpClient?.usefullAE &&
            (dbUser?.role_id === AdminRoles.InternalAdministrator ||
              dbUser?.role_id === AdminRoles.SuperAdmin) && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                Current: {corpClient.usefullAE.full_name} (
                {corpClient.usefullAE.adminRole?.name || 'No Role'})
              </div>
            )}
        </Descriptions.Item>
      </Descriptions>
    </>
  );
};

export default GeneralTab;
